import { useMemo } from 'react';
import { DISCUSS_OTHER_OPTIONS, type CohortSectionOffer } from 'CohortSectionOffer';
import { paymentSituationWithFormattedValues } from 'PaymentSituation';
import { TuitionPlanFrequencySortKey } from 'TuitionPlan';
import { additionalFinancingOptionWithFormattedValues } from './additionalFinancingOptionWithFormattedValues';
import { LOCALE_PREFIX } from './constants';

/*
    Builds the plan list for the plan selection screen: the sort order of each group, and the locale
    key each option renders with.
*/
export function usePlanSelectionOptions(cohortSectionOffer: CohortSectionOffer) {
    // Tuition plans, sorted by frequency.
    const selectablePaymentSituations = useMemo(
        () =>
            (cohortSectionOffer.studentSelectablePaymentSituations || [])
                .map(situation => {
                    const paymentSituation = paymentSituationWithFormattedValues(situation);
                    const { frequency, numIntervals } = paymentSituation.tuitionPlan;
                    const { netDiscountAmount, formattedNetDiscountAmount, formattedPaymentAmountPerInterval } =
                        paymentSituation.paymentInformation;

                    return {
                        ...paymentSituation,
                        optionId: paymentSituation.tuitionPlan.id,
                        descKey: `${LOCALE_PREFIX}.${frequency}_payment_option_desc`,
                        descValues: { numIntervals, paymentPerInterval: formattedPaymentAmountPerInterval },
                        detailsKey: `${LOCALE_PREFIX}.tuition_plan_detail_${frequency}${
                            netDiscountAmount > 0 ? '_with_discount' : ''
                        }`,
                        detailsValues: { netDiscountAmount: formattedNetDiscountAmount, numIntervals },
                    };
                })
                .sort(
                    (a, b) =>
                        TuitionPlanFrequencySortKey[a.tuitionPlan.frequency] -
                        TuitionPlanFrequencySortKey[b.tuitionPlan.frequency],
                ),
        [cohortSectionOffer],
    );

    /*
        Third-party financing offers. "Discuss other options" always sorts last and has no details
        line; the rest sort by duration.
    */
    const additionalFinancingOptions = useMemo(
        () =>
            (cohortSectionOffer.additionalFinancingOptions || [])
                .map(option => {
                    const formatted = additionalFinancingOptionWithFormattedValues(
                        cohortSectionOffer.basePaymentSituation.paymentInformation.netRequiredPayment,
                        option,
                    );
                    const isDiscussOther = option.label === DISCUSS_OTHER_OPTIONS;

                    return {
                        ...formatted,
                        optionId: option.label,
                        descKey: `${LOCALE_PREFIX}.${
                            isDiscussOther
                                ? 'additional_financing_payment_option_other_desc'
                                : 'additional_financing_payment_option_desc'
                        }`,
                        descValues: {
                            paymentPerInterval: formatted.paymentInformation.formattedPaymentAmountPerInterval,
                        },
                        detailsKey: isDiscussOther ? null : `${LOCALE_PREFIX}.additional_financing_option_details`,
                        detailsValues: {
                            numIntervals: formatted.additionalFinancingOption.monthlyDuration,
                            apr: Math.round(formatted.additionalFinancingOption.apr * 100),
                        },
                    };
                })
                .sort((a, b) => {
                    if (a.additionalFinancingOption.label === DISCUSS_OTHER_OPTIONS) return 1;
                    if (b.additionalFinancingOption.label === DISCUSS_OTHER_OPTIONS) return -1;
                    return a.additionalFinancingOption.monthlyDuration - b.additionalFinancingOption.monthlyDuration;
                }),
        [cohortSectionOffer],
    );

    const paymentOptions = useMemo(
        () => [...selectablePaymentSituations, ...additionalFinancingOptions],
        [selectablePaymentSituations, additionalFinancingOptions],
    );

    const discussOtherOptionsAvailable = additionalFinancingOptions.some(
        o => o.additionalFinancingOption.label === DISCUSS_OTHER_OPTIONS,
    );

    return { selectablePaymentSituations, additionalFinancingOptions, paymentOptions, discussOtherOptionsAvailable };
}
